'use client';

import { useEffect, useMemo } from 'react';

import { useRouter } from 'next/navigation';

import { usePages } from '@/api/usePages';
import { usePortalBoot } from '@/api/usePortalBoot';
import { getLeafSlug, getRedirectPath } from '@/lib/pageRedirectUtils';
import { PageResponse } from '@/lib/types';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';

const matchesEffectiveSlug = (
  page: PageResponse,
  effectiveSlug: string,
  pages: PageResponse[]
): boolean => {
  const pageLeaf = getLeafSlug(page.slug ?? '');

  if (effectiveSlug.includes('/')) {
    const parentPage = pages.find(
      (parent) => !parent.isSubPage && parent.id === page.parentPageId
    );
    if (parentPage) {
      return `${getLeafSlug(parentPage.slug)}/${pageLeaf}` === effectiveSlug;
    }
    const segments = effectiveSlug.split('/');
    return pageLeaf === segments[segments.length - 1];
  }

  return pageLeaf === effectiveSlug;
};

/**
 * Resolves available pages for the current auth state
 * and redirects to the best available page if the current slug is not accessible.
 *
 * @param slug - current page slug ('home' for the home page)
 */
export const useAvailablePages = ({ slug }: { slug: string }) => {
  const router = useRouter();
  const { initialBoot, playerData } = useAppState();
  const { isLoggedIn } = useLoginState();
  const { getPages } = usePages({ portalId: initialBoot?.portalId });
  const { getPortalBoot } = usePortalBoot();

  const availablePages = useMemo(() => {
    if (isLoggedIn && !playerData?.playerId) return undefined;

    if (isLoggedIn && playerData?.playerId && initialBoot?.portalId) {
      if (getPages.isFetching) return undefined;
      return getPages.data ?? undefined;
    }
    return getPortalBoot.data?.pages || initialBoot?.pages;
  }, [
    isLoggedIn,
    playerData?.playerId,
    initialBoot?.portalId,
    initialBoot?.pages,
    getPages.data,
    getPages.isFetching,
    getPortalBoot.data?.pages,
  ]);

  const effectiveSlug = slug === '' ? 'home' : slug;

  // Checked for being a list rather than merely present. These come from three
  // network payloads, and an object is truthy, so a malformed one used to pass
  // a presence check and then throw here — during render, which commits nothing
  // and leaves the player on a blank page. Unknown pages redirect nobody, which
  // is the same thing we do before they arrive.
  const pages = Array.isArray(availablePages) ? availablePages : undefined;

  const pageExists = useMemo(() => {
    if (!pages) return undefined;
    return pages.some((p) => matchesEffectiveSlug(p, effectiveSlug, pages));
  }, [pages, effectiveSlug]);

  useEffect(() => {
    if (!pages || pageExists !== false) return;
    router.replace(getRedirectPath(pages));
  }, [pages, pageExists, router]);
};
